// Pine Script lexer — produces a flat token stream

import type { Position } from '../ast/index'

export type TokenKind =
  | 'VERSION'
  | 'COMMENT'
  | 'NUMBER'
  | 'STRING'
  | 'BOOL'
  | 'NA'
  | 'IDENT'
  | 'PLUS'
  | 'MINUS'
  | 'STAR'
  | 'SLASH'
  | 'PERCENT'
  | 'CARET'
  | 'EQ'
  | 'WALRUS'
  | 'EQEQ'
  | 'NEQ'
  | 'LT'
  | 'GT'
  | 'LTE'
  | 'GTE'
  | 'AND'
  | 'OR'
  | 'NOT'
  | 'QUESTION'
  | 'COLON'
  | 'COMMA'
  | 'DOT'
  | 'LPAREN'
  | 'RPAREN'
  | 'LBRACKET'
  | 'RBRACKET'
  | 'NEWLINE'
  | 'INDENT'
  | 'DEDENT'
  | 'EOF'

export interface Token {
  kind: TokenKind
  value: string
  pos: Position
}

export class LexError extends Error {
  constructor(
    message: string,
    public pos: Position
  ) {
    super(`[${pos.line}:${pos.col}] ${message}`)
  }
}

export function lex(src: string): Token[] {
  const tokens: Token[] = []
  const lines = src.split('\n')
  const indentStack: number[] = [0]

  for (let lineNo = 0; lineNo < lines.length; lineNo++) {
    const line = lines[lineNo]
    const pos0: Position = { line: lineNo + 1, col: 1 }

    // ── Version comment ──────────────────────────────────────────────────────
    if (/^\/\/@version=/.test(line)) {
      const m = line.match(/^\/\/@version=(\d+)/)
      tokens.push({
        kind: 'VERSION',
        value: m?.[1] ?? '5',
        pos: pos0,
      })
      tokens.push({ kind: 'NEWLINE', value: '\n', pos: pos0 })
      continue
    }

    // ── Full-line comment ────────────────────────────────────────────────────
    const trimmed = line.trimStart()
    if (trimmed.startsWith('//')) {
      tokens.push({ kind: 'COMMENT', value: trimmed.slice(2).trim(), pos: pos0 })
      tokens.push({ kind: 'NEWLINE', value: '\n', pos: pos0 })
      continue
    }

    // ── Blank line ───────────────────────────────────────────────────────────
    if (trimmed === '') {
      tokens.push({ kind: 'NEWLINE', value: '\n', pos: pos0 })
      continue
    }

    // ── Indentation ──────────────────────────────────────────────────────────
    const indent = line.length - trimmed.length
    const prevIndent = indentStack[indentStack.length - 1] ?? 0

    if (indent > prevIndent) {
      indentStack.push(indent)
      tokens.push({ kind: 'INDENT', value: '', pos: pos0 })
    } else {
      while ((indentStack[indentStack.length - 1] ?? 0) > indent) {
        indentStack.pop()
        tokens.push({ kind: 'DEDENT', value: '', pos: pos0 })
      }
    }

    // ── Tokenize line body ───────────────────────────────────────────────────
    let col = indent + 1
    let i = indent

    while (i < line.length) {
      const ch = line[i]
      const pos: Position = { line: lineNo + 1, col }

      // Skip whitespace
      if (ch === ' ' || ch === '\t') {
        i++
        col++
        continue
      }

      // Inline comment
      if (line[i] === '/' && line[i + 1] === '/') {
        break
      }

      // String literals
      if (ch === '"' || ch === "'") {
        const quote = ch
        let s = ''
        i++
        col++
        while (i < line.length && line[i] !== quote) {
          s += line[i]
          i++
          col++
        }
        i++ // closing quote
        col++
        tokens.push({ kind: 'STRING', value: s, pos })
        continue
      }

      // Numbers
      if (/\d/.test(ch) || (ch === '.' && /\d/.test(line[i + 1] ?? ''))) {
        let num = ''
        while (i < line.length && /[\d.]/.test(line[i] ?? '')) {
          num += line[i]
          i++
          col++
        }
        tokens.push({ kind: 'NUMBER', value: num, pos })
        continue
      }

      // Identifiers and keywords
      if (/[a-zA-Z_]/.test(ch)) {
        let ident = ''
        while (i < line.length && /[\w]/.test(line[i] ?? '')) {
          ident += line[i]
          i++
          col++
        }
        if (ident === 'true' || ident === 'false') {
          tokens.push({ kind: 'BOOL', value: ident, pos })
        } else if (ident === 'na') {
          tokens.push({ kind: 'NA', value: 'na', pos })
        } else if (ident === 'and') {
          tokens.push({ kind: 'AND', value: 'and', pos })
        } else if (ident === 'or') {
          tokens.push({ kind: 'OR', value: 'or', pos })
        } else if (ident === 'not') {
          tokens.push({ kind: 'NOT', value: 'not', pos })
        } else {
          tokens.push({ kind: 'IDENT', value: ident, pos })
        }
        continue
      }

      // Two-char operators
      const two = (line[i] ?? '') + (line[i + 1] ?? '')
      if (two === ':=') {
        tokens.push({ kind: 'WALRUS', value: ':=', pos })
        i += 2
        col += 2
        continue
      }
      if (two === '==') {
        tokens.push({ kind: 'EQEQ', value: '==', pos })
        i += 2
        col += 2
        continue
      }
      if (two === '!=') {
        tokens.push({ kind: 'NEQ', value: '!=', pos })
        i += 2
        col += 2
        continue
      }
      if (two === '<=') {
        tokens.push({ kind: 'LTE', value: '<=', pos })
        i += 2
        col += 2
        continue
      }
      if (two === '>=') {
        tokens.push({ kind: 'GTE', value: '>=', pos })
        i += 2
        col += 2
        continue
      }

      // Single-char tokens
      const single: Record<string, TokenKind> = {
        '+': 'PLUS',
        '-': 'MINUS',
        '*': 'STAR',
        '/': 'SLASH',
        '%': 'PERCENT',
        '^': 'CARET',
        '=': 'EQ',
        '<': 'LT',
        '>': 'GT',
        '?': 'QUESTION',
        ':': 'COLON',
        ',': 'COMMA',
        '.': 'DOT',
        '(': 'LPAREN',
        ')': 'RPAREN',
        '[': 'LBRACKET',
        ']': 'RBRACKET',
      }
      if (ch in single) {
        tokens.push({ kind: single[ch], value: ch, pos })
        i++
        col++
        continue
      }

      // Unknown char — skip
      i++
      col++
    }

    tokens.push({ kind: 'NEWLINE', value: '\n', pos: pos0 })
  }

  // Close any remaining indents
  while (indentStack.length > 1) {
    indentStack.pop()
    tokens.push({ kind: 'DEDENT', value: '', pos: { line: lines.length, col: 1 } })
  }

  tokens.push({ kind: 'EOF', value: '', pos: { line: lines.length + 1, col: 1 } })
  return tokens
}
